/**
 * 版本检查工具
 * 用于定期检查应用是否有新版本，并提示用户刷新
 */

import { toast } from 'sonner';
import { APP_VERSION } from '@/constants/version';
import { addMsgOnReloaded } from './msgOnReload';

interface VersionInfo {
  version: string;
  timestamp: number;
  buildTime: string;
}

// 当前运行中的版本必须来自构建产物，不能用远端 version.json 覆盖。
// 否则初始化后再次检查时，两边永远相同，无法发现新版本。
const currentVersion = APP_VERSION;
let lastNotifyId: string | number | undefined;
const DEFAULT_THROTTLE_INTERVAL = 10 * 1000;
const RESOURCE_RECOVERY_KEY = 'management-resource-recovery-version';
let resourceRecoveryAttempted = false;
let resourceRecoveryPromise: Promise<{
  hasUpdate: boolean;
  reloaded: boolean;
}> | null = null;
let lastCheckTime = 0;
let lastCheckResult = false;

function getVersionRequestUrl() {
  const baseUrl = import.meta.env.BASE_URL || '/';
  const versionPath = `${baseUrl.replace(/\/?$/, '/')}version.json`;
  return `${versionPath}?t=${Date.now()}`;
}

async function getRemoteVersion(): Promise<VersionInfo> {
  const response = await fetch(getVersionRequestUrl(), { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(`版本信息请求失败: ${response.status}`);
  }

  const data: VersionInfo = await response.json();
  if (!data.version) {
    throw new Error('版本信息格式无效');
  }

  return data;
}

export async function initVersionCheck(): Promise<boolean> {
  // 新版本成功启动后，允许下一次资源错误重新进行一次恢复尝试。
  try {
    if (sessionStorage.getItem(RESOURCE_RECOVERY_KEY) !== currentVersion) {
      sessionStorage.removeItem(RESOURCE_RECOVERY_KEY);
    }
  } catch {
    // 某些隐私模式下 sessionStorage 可能不可用，不影响版本检查。
  }

  console.log(`当前应用构建版本: v${currentVersion}`);
  // 初始化阶段也执行一次真实比较，确保发布后首次打开页面就能提示更新。
  const hasUpdate = await checkForUpdate(0);
  if (hasUpdate) {
    notifyUpdate();
  }
  return hasUpdate;
}

export async function checkForUpdate(
  throttleMs: number = DEFAULT_THROTTLE_INTERVAL
): Promise<boolean> {
  const now = Date.now();

  if (now - lastCheckTime < throttleMs) {
    return lastCheckResult;
  }

  lastCheckTime = now;

  try {
    const data = await getRemoteVersion();

    lastCheckResult = data.version !== currentVersion;

    if (lastCheckResult) {
      console.log(`发现新版本: v${data.version}（当前: v${currentVersion}）`);
    }

    return lastCheckResult;
  } catch (error) {
    console.warn('版本检查失败:', error);
    lastCheckResult = false;
    lastCheckTime = 0;
    return false;
  }
}

/** 判断是否为发布新版本后旧 chunk 已失效导致的懒加载错误。 */
export function isDynamicImportError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message)
        : String(error ?? '');
  const normalizedMessage = message.toLowerCase();

  return (
    normalizedMessage.includes('failed to fetch dynamically imported module') ||
    normalizedMessage.includes('importing a module script failed') ||
    normalizedMessage.includes('chunkloaderror') ||
    (normalizedMessage.includes('loading chunk') && normalizedMessage.includes('failed'))
  );
}

/**
 * 处理旧 chunk 加载失败。
 * 只有确认远端已有新版本时才自动刷新，并用 sessionStorage 限制为当前构建最多一次，
 * 防止 CDN 或入口文件异常时出现无限刷新。
 */
async function recoverFromResourceLoadErrorInternal(error: unknown): Promise<{
  hasUpdate: boolean;
  reloaded: boolean;
}> {
  if (!isDynamicImportError(error)) {
    return { hasUpdate: false, reloaded: false };
  }

  const hasUpdate = await checkForUpdate(0);

  try {
    if (sessionStorage.getItem(RESOURCE_RECOVERY_KEY) === currentVersion) {
      if (hasUpdate) {
        notifyUpdate();
      }
      return { hasUpdate, reloaded: false };
    }
  } catch {
    // 某些隐私模式下读取 sessionStorage 可能失败，继续使用内存标记。
  }

  if (resourceRecoveryAttempted) {
    if (hasUpdate) {
      notifyUpdate();
    }
    return { hasUpdate, reloaded: false };
  }

  resourceRecoveryAttempted = true;
  try {
    sessionStorage.setItem(RESOURCE_RECOVERY_KEY, currentVersion);
  } catch {
    // 无法写入 sessionStorage 时使用内存标记，避免本次运行期间无限刷新。
  }

  window.setTimeout(() => window.location.reload(), 100);
  return { hasUpdate, reloaded: true };
}

export function recoverFromResourceLoadError(error: unknown): Promise<{
  hasUpdate: boolean;
  reloaded: boolean;
}> {
  if (resourceRecoveryPromise) {
    return resourceRecoveryPromise;
  }

  resourceRecoveryPromise = recoverFromResourceLoadErrorInternal(error).finally(() => {
    resourceRecoveryPromise = null;
  });

  return resourceRecoveryPromise;
}

/**
 * 监听未被 React Router 或 ErrorBoundary 捕获的懒加载错误。
 * 动态 import 失败可能只触发浏览器的 unhandledrejection，不能只依赖 React 错误边界。
 */
export function setupResourceLoadRecovery(): () => void {
  const handleError = (event: ErrorEvent) => {
    const error = event.error ?? event.message;
    if (isDynamicImportError(error)) {
      void recoverFromResourceLoadError(error);
    }
  };

  const handleRejection = (event: PromiseRejectionEvent) => {
    if (isDynamicImportError(event.reason)) {
      void recoverFromResourceLoadError(event.reason);
    }
  };

  window.addEventListener('error', handleError, true);
  window.addEventListener('unhandledrejection', handleRejection);

  return () => {
    window.removeEventListener('error', handleError, true);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
}

export function notifyUpdate(): void {
  if (lastNotifyId) {
    toast.dismiss(lastNotifyId);
  }

  lastNotifyId = toast.success('发现新版本', {
    description: '检测到新版本，点击“立即更新”刷新页面',
    duration: Infinity,
    action: {
      label: '立即更新',
      onClick: () => {
        addMsgOnReloaded({
          type: 'success',
          content: '已更新到新版本',
        });
        window.location.reload();
      },
    },
  });
}

export function startVersionPolling(
  interval: number = 5 * 60 * 1000,
  autoPrompt = true,
  checkImmediately = true
): () => void {
  const checkUpdate = async () => {
    const hasUpdate = await checkForUpdate();

    if (hasUpdate && autoPrompt) {
      notifyUpdate();
    }
  };

  if (checkImmediately) {
    void checkUpdate();
  }
  const timerId = window.setInterval(checkUpdate, interval);

  return () => {
    window.clearInterval(timerId);
  };
}

export function manualCheckUpdate(): Promise<void> {
  return checkForUpdate().then((hasUpdate) => {
    if (hasUpdate) {
      notifyUpdate();
    }
  });
}

export function checkOnVisible(): () => void {
  const onVisibilityChange = () => {
    if (!document.hidden) {
      void manualCheckUpdate();
    }
  };

  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      void manualCheckUpdate();
    }
  };

  const onFocus = () => {
    void manualCheckUpdate();
  };

  if (typeof document.hidden !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('focus', onFocus);

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('focus', onFocus);
  };
}
